// Ferry markers (EU 561/2006 Art. 9) are what let a work day end at a rest far
// shorter than the eight-hour gap: a rest interrupted by driving onto or off a
// boat still counts as one rest. The markers are FERRY_START / FERRY_END points
// on the tachograph, so a crossing is a pair — and a marker the tachograph
// never closed is exactly the case that makes a day split look unexplained.

import { parseISO } from 'date-fns'

export interface FerryMarker {
  id: string
  type: string
  timestamp: string
}

export interface FerrySegment {
  id: string
  start: Date
  /** null when no counterpart marker closes this one. */
  end: Date | null
  /** Which half is missing, for a marker that never got its pair. */
  unpaired: 'start' | 'end' | null
}

const FERRY_START = 'FERRY_START'
const FERRY_END = 'FERRY_END'

/**
 * Pair ferry markers into crossings, in time order.
 *
 * A START opens a crossing and the next END closes it. A START while one is
 * already open leaves the earlier one unpaired rather than swallowing it, and
 * an END with nothing open is unpaired too — both are worth seeing.
 */
export const buildFerrySegments = (
  markers: readonly FerryMarker[]
): FerrySegment[] => {
  const ordered = [...markers]
    .map(marker => ({ marker, at: parseISO(marker.timestamp) }))
    .sort((a, b) => a.at.getTime() - b.at.getTime())

  const segments: FerrySegment[] = []
  let open: { id: string; at: Date } | null = null

  const pushOpenAsUnpaired = () => {
    if (!open) return
    segments.push({
      id: open.id,
      start: open.at,
      end: null,
      unpaired: 'end',
    })
    open = null
  }

  for (const { marker, at } of ordered) {
    if (marker.type === FERRY_START) {
      pushOpenAsUnpaired()
      open = { id: marker.id, at }
      continue
    }
    if (marker.type !== FERRY_END) continue

    if (open) {
      segments.push({ id: open.id, start: open.at, end: at, unpaired: null })
      open = null
    } else {
      segments.push({ id: marker.id, start: at, end: null, unpaired: 'start' })
    }
  }

  pushOpenAsUnpaired()

  return segments.sort((a, b) => a.start.getTime() - b.start.getTime())
}

export interface FerryPin {
  id: string
  date: Date
  /** Boarding or disembark, whichever end of the crossing this marker is. */
  kind: 'start' | 'end'
  segment: FerrySegment
}

/**
 * The points a crossing is pinned at: one for boarding, one for disembark.
 *
 * A segment with no counterpart yields the single marker it does have -- and
 * for `unpaired: 'start'` that marker is the disembark, since an end with
 * nothing open is stored in the segment's `start` field.
 */
export const buildFerryPins = (
  segments: readonly FerrySegment[]
): FerryPin[] => {
  const pins = segments.flatMap<FerryPin>(segment => {
    if (segment.unpaired === 'start') {
      return [
        { id: `${segment.id}-end`, date: segment.start, kind: 'end', segment },
      ]
    }

    const boarding: FerryPin = {
      id: `${segment.id}-start`,
      date: segment.start,
      kind: 'start',
      segment,
    }

    return segment.end
      ? [
          boarding,
          {
            id: `${segment.id}-end`,
            date: segment.end,
            kind: 'end',
            segment,
          },
        ]
      : [boarding]
  })

  return pins.sort((a, b) => a.date.getTime() - b.date.getTime())
}
